import { observer } from 'mobx-react-lite'
import { useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { StyleSheet, View, ViewProps } from 'react-native'
import { DataWithUpload, Draft, FloorplanData, FloorplanPosition, useStore } from 'zens-store'
import {
  Banner,
  Button,
  Color,
  Icon,
  Radius,
  Scaler,
  Spacing,
  Text,
  TextList,
  UploadFile,
  UploadFileError,
  ZoomValue,
  ZoomView,
} from 'zens-storybook'

import FloorplanFloorForm from './floorform'
import FloorplanAsset from './asset'

const styles = StyleSheet.create({
  container: {
    flex: 1,
    flexDirection: 'row',
  },
  content: {
    flex: 1,
  },
  menu: {
    // @ts-expect-error Flex initial not supported
    flex: 'initial',
    padding: Spacing.M,
    borderColor: Color.calmGrey,
    borderRightWidth: 1,
  },
  zoom: {
    flex: 1,
    paddingHorizontal: Spacing.S,
    paddingVertical: 76 + Spacing.S,
  },
  scaler: {
    ...StyleSheet.absoluteFillObject,
    zIndex: 200,
  },
  message: {
    flex: 1,
    flexDirection: 'row',
  },
  info: {
    marginLeft: Spacing.S,
  },
  infoText: {
    color: Color.simplyWhite,
  },
  error: {
    fontSize: 14,
    marginTop: Spacing.XXS,
    color: Color.soothingPink,
    margin: Spacing.S,
  },
  banner: {
    position: 'absolute',
    zIndex: 300,
    top: 0,
    left: 0,
    right: 0,
  },
  bottom: {
    position: 'absolute',
    left: 50 + Spacing.S,
    right: 0,
    bottom: Spacing.S,
    alignItems: 'center',
  },
  upload: {
    flex: 1,
    padding: Spacing.XXL,
  },
  title: {
    marginBottom: Spacing.L,
  },
  placeholder: {
    marginTop: Spacing.L,
  },
  skeletonEditor: {
    margin: Spacing.S,
    backgroundColor: Color.sleepingGrey,
    borderRadius: Radius.S,
  },
})

interface Props extends ViewProps {
  draft: Draft<DataWithUpload<FloorplanData>>
  onSave?: (draft: Draft<DataWithUpload<FloorplanData>>) => Promise<void>
  onCancel?: (draft: Draft<FloorplanData>) => Promise<void>
  onDelete?: (draft: Draft<FloorplanData>) => Promise<void>
  errors?: Record<string, string>
}

const FloorplanSettings = (props: Props) => {
  const { draft, onSave, onCancel, onDelete, errors, style, ...otherProps } = props
  const [zoom, setZoom] = useState<Partial<ZoomValue>>({})
  const [error, setError] = useState<string>()
  const { message } = useStore()
  const { t } = useTranslation()
  const { scale, background } = draft.data.floorplan || {}
  const value = [...(scale?.start ? [scale.start] : []), ...(scale?.end ? [scale.end] : [])]
  const hasBackground = !!draft.data.floorplan?.background
  const hasScale =
    typeof draft.data.floorplan?.scale?.start?.x === 'number' &&
    typeof draft.data.floorplan?.scale?.start?.y === 'number' &&
    typeof draft.data.floorplan?.scale?.end?.x === 'number' &&
    typeof draft.data.floorplan?.scale?.end?.y === 'number'

  function updateScaleError(errors?: Record<string, string>) {
    const error = Object.keys({ ...errors }).find(
      (key) =>
        key === 'floorplan.scale' || key.startsWith('floorplan.scale.start') || key.startsWith('floorplan.scale.end'),
    )
      ? t('Scale line is required. Click on the floorplan to draw a line.')
      : undefined
    if (error) {
      message.add({
        message: error,
        status: 'error',
      })
    }
  }

  function updateScale(value: FloorplanPosition[]) {
    draft.update('floorplan', {
      ...draft.data.floorplan,
      scale: {
        start: value[0],
        end: value[1],
        distance: draft.data.floorplan?.scale?.distance,
      },
    })
  }

  function updateUploadError(error?: UploadFileError) {
    let message
    switch (error) {
      case UploadFileError.FileInvalidType:
        message = t('Image type must be SVG')
        break
      case UploadFileError.FileTooLarge:
        message = t('Image too large')
        break
      default:
        message = t('Image selection failed')
    }
    setError(message)
  }

  function updateBackground(value?: string, file?: File) {
    draft.update('upload', file)
    draft.update('floorplan', { ...draft.data.floorplan, background: value })
    setError(undefined)
  }

  function removeBackground() {
    draft.update('upload', null)
    draft.update('floorplan', { ...draft.data.floorplan, background: null })
  }

  function renderScaler() {
    return (
      <>
        {!hasScale && (
          <Banner
            status="info"
            closeable
            message={
              <View style={styles.message}>
                <Icon name="info" color={Color.simplyWhite} />
                <Text style={[styles.info, styles.infoText]}>
                  <Trans>
                    Click on the floorplan on to draw a line of which you can give the correct dimensions. Tip: larger
                    measure will result in more accurate scaling. Select for example a wall that is measured to be at
                    least 500 cm.
                  </Trans>
                </Text>
              </View>
            }
            style={styles.banner}
          />
        )}
        <ZoomView
          style={styles.zoom}
          onZoom={setZoom}
          overlay={
            <View style={styles.bottom}>
              <Button
                light
                left="trash"
                title={t('Remove floorplan')}
                onPress={removeBackground}
                testID="FloorplanSettings.RemoveBackground"
              />
            </View>
          }
        >
          <FloorplanAsset uri={background} width={4000} />
          <Scaler
            style={styles.scaler}
            color={Color.greyBlue}
            scale={zoom.scale}
            value={value}
            onComplete={updateScale}
            testID="FloorplanSettings.Scaler"
          />
        </ZoomView>
      </>
    )
  }

  function renderUpload() {
    return (
      <>
        <Banner
          status="info"
          closeable
          message={
            <View style={styles.message}>
              <Icon name="info" color={Color.simplyWhite} />
              <TextList
                style={styles.info}
                textStyle={styles.infoText}
                data={[
                  t('Upload a base image that only include walls, doors and non-moving objects'),
                  t('SVG files only to ensure scalability'),
                  t('Only upload files in greyscale to suit workspaces'),
                ]}
              />
            </View>
          }
        />
        <View style={styles.upload}>
          <UploadFile
            title={
              <Text level={4} style={styles.title}>
                <Trans>Upload floorplan</Trans>
              </Text>
            }
            placeholder={
              <Text style={styles.placeholder}>
                <Trans>Drag and drop or click to upload</Trans>
              </Text>
            }
            options={{
              accept: { 'image/svg+xml': [] },
              minSize: 0,
            }}
            deleteTitle={t('Delete image')}
            value={draft.data.floorplan?.background}
            onValueChange={updateBackground}
            error={!!error}
            onError={updateUploadError}
            progress={draft.data.progress}
          />
          {error && (
            <Text style={styles.error} testID="FormField.Error">
              {error}
            </Text>
          )}
        </View>
      </>
    )
  }
  return (
    <View style={[styles.container, style]} {...otherProps}>
      <FloorplanFloorForm
        draft={draft}
        style={styles.menu}
        onSave={onSave}
        onCancel={onCancel}
        onDelete={!isNaN(draft.data.id) ? onDelete : undefined}
        onError={updateScaleError}
        errors={errors}
      />
      <View style={styles.content}>{hasBackground ? renderScaler() : renderUpload()}</View>
    </View>
  )
}

interface SkeletonProps extends ViewProps {}
const Skeleton = (props: SkeletonProps) => {
  const { style, ...otherProps } = props
  return (
    <View style={[styles.container, style]} {...otherProps}>
      <FloorplanFloorForm.Skeleton style={styles.menu} />
      <View style={[styles.zoom, styles.skeletonEditor]} />
    </View>
  )
}
FloorplanSettings.Skeleton = Skeleton

export default observer(FloorplanSettings)
